import type { VaultProvider } from '../vaults/fetchVaultsAll'
import type {
  MaturityTerms,
  RateKind,
  SupplyExitMode,
  TermSheet,
  TermSheetDigest,
} from '../terms/types'
import { vaultTraits } from '../terms/vault'
import type {
  EarnAmount,
  EarnAvailability,
  EarnBasket,
  EarnBasketLeg,
  EarnExit,
  EarnGating,
  EarnMarket,
  EarnRate,
  EarnRateSource,
  EarnRisk,
} from './types'
import { buildVaultEarnUid, vaultVenue } from './uid'
import {
  isIlliquid,
  resolveEarnIdentity,
  stripLeadingBrand,
  trancheFromCounterparty,
  venueBrand,
  withMaturityLabel,
  withTrancheLabel,
} from './labels'
import { denomination, formatRaw, num, str, yieldProfile } from './normalize'

/**
 * Vault half of the `/earn` normalizer: one `/vaults/latest` row → `EarnMarket`.
 *
 * The input is the recorder origin's item shape, which is loosely typed by
 * design (providers add fields without a schema bump), so every read goes
 * through a tolerant accessor and a missing field yields `undefined` rather
 * than a throw. A row missing its two load-bearing identifiers — the vault
 * address and the underlying — is DROPPED, not patched.
 *
 * See EARN_ENDPOINT_PLAN.md §4.
 */

/** The origin's `/vaults/latest` item. Permissive on purpose — see above. */
export interface VaultSourceRow {
  provider?: string
  vaultAddress?: string
  underlying?: string
  symbol?: string
  name?: string
  displayName?: string
  decimals?: number
  assetDecimals?: number
  curatorName?: string
  curatorEntity?: string
  /**
   * The vault origin names this `rating`, not `risk`, and uses `level` where
   * pools use `label`. Two shapes for one concept — read both explicitly
   * rather than assuming either.
   */
  rating?: { level?: string; score?: number | string }
  sharePrice?: number | string
  sharePriceUsd?: number | string
  rates?: {
    depositRate?: number | string
    rewardsRate?: number | string
    totalRate?: number | string
    supplyRate?: number | string
    fee?: number | string
  }
  tvl?: {
    totalAssets?: string | number
    totalAssetsFormatted?: number | string
    totalSupply?: string | number
    totalAssetsUsd?: number | string
  }
  liquidity?: {
    liquidity?: string | number
    liquidityFormatted?: number | string
    liquidityUsd?: number | string
  }
  underlyingInfo?: {
    asset?: { symbol?: string; decimals?: number; logoURI?: string }
    prices?: { priceUsd?: number | string }
  }
  vaultInfo?: {
    symbol?: string
    name?: string
    logoURI?: string
    assetGroup?: string
    yieldProfile?: string
    denomination?: string
  }
  providerMeta?: Record<string, any>
  [key: string]: unknown
}

/**
 * Providers whose rate fields arrive as FRACTIONS (`0.0412`) rather than
 * percent (`4.12`), and therefore need scaling.
 *
 * **EMPTY for the origin path, and that is the verified answer, not a
 * default.** The underlying hazard is real — `vaults/DATABASE_INTEGRATION.md`
 * §1 documents HyperCore's `apr` and GMX's `apy`/`baseApy`/`bonusApr` as
 * fractions where every 4626 provider reports percent — but the recorder
 * already resolves it before the row reaches us. `mapGmxToListing` /
 * `mapHypercoreToListing` in the origin's `routes/vaults.ts` run every rate
 * through `pct()` (`× 100`) and park the raw fraction under
 * `providerMeta.apr`, explicitly "for sort/filter parity".
 *
 * So scaling here would DOUBLE-convert and inflate every GMX and HyperCore row
 * 100× — the exact bug this constant was written to prevent, in the opposite
 * direction.
 *
 * The set survives as a parameter rather than being deleted because the
 * SDK-fed path has the opposite convention: `getVaultPublicDataAll` emits the
 * raw provider fractions with no `pct()` in between. Anything reading the SDK
 * directly (a recorder, a `?source=live` fallback) must pass
 * {@link SDK_FRACTION_RATE_PROVIDERS}.
 */
export const FRACTION_RATE_PROVIDERS: ReadonlySet<string> = new Set<string>()

/**
 * The fraction-reporting providers **as the SDK emits them**, before the
 * recorder's `pct()` pass. Pass this to {@link earnMarketFromVault} when the
 * rows come from `getVaultPublicDataAll` rather than from `/vaults/latest`.
 */
export const SDK_FRACTION_RATE_PROVIDERS: ReadonlySet<string> = new Set([
  'hypercore',
  'gmx',
])

/** Per-call overrides for sources that disagree with the origin's conventions. */
export interface EarnVaultNormalizeOptions {
  /**
   * Reserved. Term sheets are stamped on the RETURNED PAGE by the worker's
   * `stampEarnTerms`, not here — building one per row during the merge would
   * pay for ~1000 sheets to serve 50, and would miss the origin-proxy path
   * entirely, since those rows never pass through this normalizer.
   */
  terms?: 'none' | 'digest' | 'full'
  /**
   * Providers whose rates need `× 100`. Defaults to
   * {@link FRACTION_RATE_PROVIDERS} (empty — the origin already normalized).
   */
  fractionRateProviders?: ReadonlySet<string>
}

/**
 * Rate provenance per provider — WHERE the number came from. Sorting a mixed
 * list on `rate.total` is only honest if the consumer can see that a
 * `realized` 12 % and a `chain` 12 % are different claims.
 */
/**
 * WHERE a provider's rate comes from — exported because the recorder builds
 * the same row shape from SQL and had no way to say anything but `'chain'`,
 * so every served row claimed an on-chain read including the ten providers
 * whose number is an API's. A per-provider fact restated in two languages is
 * the drift this repo has paid for before (the 100× GMX scaling); one map,
 * two importers.
 */
export const EARN_RATE_SOURCE_BY_PROVIDER: Record<string, EarnRateSource> = {
  morpho: 'api',
  silo: 'api',
  lagoon: 'api',
  hypercore: 'api',
  gmx: 'api',
  upshift: 'api',
  yearn: 'api',
  'aave-earn': 'api',
  pendle: 'api',
  spectra: 'api',
  lista: 'chain',
  fluid: 'chain',
  gearbox: 'chain',
  'euler-earn': 'chain',
  termmax: 'chain',
  lst: 'chain',
  savings: 'chain',
}

/**
 * Rate MECHANISM per provider, from TERM_SHEET_PLAN §7.
 *
 * `gmx` / `hypercore` map to `'realized'` deliberately: they have no rate
 * mechanism at all. Their headline number is a trading outcome measured over a
 * past window, not a rate anything sets — which is exactly why they are
 * excluded from share-price APR and classified `volatile`. A `variable-*` kind
 * would imply a curve that does not exist, and `'none'` would say they pay
 * nothing, which is a different false claim.
 *
 * Savings is the loose one: most rows are governance-set (`variable-managed`),
 * but the NAV-attested ones (Re, Apyx) are `nav-accrual` — resolved per row in
 * `resolveRateKind` from the `navOracle` / `solvency` the vault now carries.
 */
const RATE_KIND: Record<string, RateKind> = {
  morpho: 'variable-curve',
  lista: 'variable-curve',
  silo: 'variable-curve',
  'euler-earn': 'variable-curve',
  fluid: 'variable-curve',
  gearbox: 'variable-curve',
  'aave-earn': 'variable-curve',
  // The TermMax VAULT is a perpetual curated 4626 whose curator rolls
  // fixed-term FT positions — the vault itself has no maturity and no locked
  // rate. `fixed-term` here claimed both, producing a "fixed" rate with no date
  // attached to it. The LENDER `TERMMAX_*` is fixed-term; this is not.
  termmax: 'variable-managed',
  // A PT's implied APY is locked in at purchase and realized only by holding
  // to maturity — the same promise TermMax's FT makes, reached by a different
  // mechanism (an AMM discount rather than a quoted curve).
  pendle: 'fixed-term',
  // Same instrument, same promise: the discount at purchase IS the rate.
  spectra: 'fixed-term',
  savings: 'variable-managed',
  lst: 'variable-managed',
  lagoon: 'variable-managed',
  upshift: 'variable-managed',
  yearn: 'variable-managed',
  hypercore: 'realized',
  gmx: 'realized',
}

/**
 * The exit mode for a row that does not declare its own.
 *
 * Delegates to the term-sheet provider traits rather than keeping a second
 * table: this value decides what a user reads as "my money is available", and
 * it is ALSO the gate on the illiquidity heuristic (`isIlliquid` only fires on
 * same-block modes), so two tables drifting apart would be invisible and
 * consequential in both directions.
 *
 * The important case is the ERC-4626 vaults over lending markets. They default
 * to `instant-capped`, not `instant`: they hold a fraction of TVL as cash and
 * the rest as supply positions, so an exit above the liquid balance cannot
 * settle this block. `instant` is claimed back only where the row PROVES full
 * liquidity — an unreported liquidity is not a full one.
 */
function resolveExitMode(
  provider: string,
  meta: Record<string, any>,
  tvl: VaultSourceRow['tvl'],
  liq: VaultSourceRow['liquidity'],
): SupplyExitMode {
  const declared = str(meta.withdrawalMode) as SupplyExitMode | undefined
  if (declared) return declared

  const fallback = vaultTraits(provider)?.defaultExitMode ?? 'instant'
  if (fallback !== 'instant-capped') return fallback

  const total = num(tvl?.totalAssetsFormatted) ?? num(tvl?.totalAssetsUsd)
  const available = num(liq?.liquidityFormatted) ?? num(liq?.liquidityUsd)
  if (total === undefined || available === undefined) return 'instant-capped'
  if (total <= 0) return 'instant'
  return available >= total ? 'instant' : 'instant-capped'
}

/**
 * Normalize one origin vault row. Returns `undefined` when the row lacks the
 * identifiers needed to key or transact it — a dropped row is recoverable, a
 * row with a fabricated key is not.
 */
/**
 * The basket descriptor for a VAULT row.
 *
 * GMX GM/GLV is the case that exists today and the one that proves the shape
 * is not Fluid-specific: a GM market token is backed by a LONG leg and a SHORT
 * leg, the split moves with the pool's inventory and the trader PnL it
 * absorbs, and a holder cannot take one leg out on its own. That is the same
 * claim `autoBalanced` makes on a Fluid smart vault, arrived at through
 * completely different machinery.
 *
 * The two differ in exactly one published way, which is why `rowAsset` exists:
 * a GM row IS the position (`asset` is the GM token), while Fluid emits one
 * row per leg.
 *
 * A Curve or Uniswap LP vault drops in here with no new vocabulary — set
 * `pool`, list the legs in the pool's index order, done. Balancer's eight-token
 * pools work for the same reason `legs` is an array.
 */
function resolveVaultBasket(
  provider: string,
  meta: Record<string, any>,
): EarnBasket | undefined {
  if (provider !== 'gmx') return undefined
  const legs: EarnBasketLeg[] = []
  const push = (address?: string, symbol?: string) => {
    const a = str(address)?.toLowerCase()
    if (a)
      legs.push({ address: a, ...(str(symbol) ? { symbol: str(symbol) } : {}) })
  }
  // LONG first, then SHORT — GMX's own order, and the order its deposit
  // builder takes its two amounts in. Never sort it.
  push(meta.longToken, meta.longSymbol)
  push(meta.shortToken, meta.shortSymbol)
  if (legs.length === 0) return undefined

  return {
    rowAsset: 'positionUnit',
    legs,
    autoBalanced: true,
    positionUnit: { kind: 'lpToken', decimals: 18 },
    pool: 'gmx-gm',
  }
}

export function earnMarketFromVault(
  row: VaultSourceRow,
  chainId: string,
  opts: EarnVaultNormalizeOptions = {},
): EarnMarket | undefined {
  const provider = str(row.provider)
  const address = str(row.vaultAddress)?.toLowerCase()
  const underlying = str(row.underlying)?.toLowerCase()
  if (!provider || !address || !underlying) return undefined

  const meta = row.providerMeta ?? {}
  const basket = resolveVaultBasket(provider, meta)
  const info = row.vaultInfo ?? {}
  const rates = row.rates ?? {}
  const tvl = row.tvl ?? {}
  const liq = row.liquidity ?? {}

  // Share decimals and ASSET decimals are not the same number (Lagoon runs
  // 18-decimal shares over 6-decimal USDC). `tvl.totalAssets` is denominated
  // in the ASSET, so it must be formatted with asset decimals.
  const shareDecimals = num(row.decimals) ?? 18
  const assetDecimals =
    num(row.assetDecimals) ??
    num(row.underlyingInfo?.asset?.decimals) ??
    shareDecimals

  const toPercent = (v: unknown) =>
    ratePercent(v, provider, opts.fractionRateProviders)
  const rewards = toPercent(rates.rewardsRate)
  const deposit = toPercent(rates.depositRate)
  // `depositRate` is the SUM (`supplyRate + rewardsRate`) on every provider
  // type that documents it, so it is not the base leg. Reading it as one made
  // `rate.base` — documented as "protocol interest" — report the all-in figure
  // on every incentivised vault, and made `base + rewards` count the incentives
  // twice. Where `supplyRate` is absent the base is recovered from that same
  // documented identity rather than guessed.
  const base =
    toPercent(rates.supplyRate) ??
    (deposit !== undefined && rewards !== undefined
      ? deposit - rewards
      : deposit)
  // Prefer the origin's own all-in figure; fall back to summing the legs so a
  // provider that omits `totalRate` still ranks, rather than reading 0 %.
  const total = toPercent(rates.totalRate) ?? deposit ?? sum(base, rewards) ?? 0

  // Maturity FIRST: the rate a matured row may publish is zero, and building
  // the rate before knowing the maturity is precisely how a dead fixed rate
  // reached the top of an APR sort. See `earnRateAtMaturity`.
  const maturity = resolveMaturity(meta)

  const rate: EarnRate = earnRateAtMaturity(
    {
      total,
      base,
      rewards,
      // A vault has no separate intrinsic leg — whatever it pays IS the venue's
      // own yield. Leaving this undefined rendered an empty "Venue APR" on every
      // vault row, which read as "this vault pays nothing".
      marketOwn: total,
      // ...and for the same reason it can never be pass-through.
      passthrough: false,
      kind: resolveRateKind(provider, meta),
      source: EARN_RATE_SOURCE_BY_PROVIDER[provider] ?? 'api',
      // The curator's cut. Dropped entirely until now, which left the earn row
      // unable to say why its net rate sits below the gross one — and left the
      // term sheet built from this row with no fee schedule at all.
      fee: toPercent(rates.fee),
    },
    maturity,
  )

  const availability = resolveAvailability(meta, maturity)
  const exitMode = resolveExitMode(provider, meta, tvl, liq)

  const market: EarnMarket = {
    earnUid: buildVaultEarnUid(provider as VaultProvider, chainId, address),
    chainId,
    venue: vaultVenue(provider as VaultProvider),
    venueKind: 'vault',
    // Curator first (it names the actual operator), then the provider brand.
    brand:
      str(row.curatorName) ?? venueBrand(vaultVenue(provider as VaultProvider)),
    // What the vault is BUILT ON, kept apart from who runs it — see
    // `resolveEarnIdentity` for why this is not simply provider-vs-brand.
    ...withCuratorEntity(
      resolveEarnIdentity(
        vaultVenue(provider as VaultProvider),
        str(row.curatorName),
      ),
      str(row.curatorEntity),
    ),
    // The maturity is part of a fixed-term row's IDENTITY. Pendle ships names
    // with no date, so several PTs on one underlying render identically —
    // different bonds at different prices under one label.
    // **The vault's OWN name first.** `vaultInfo.name` / `displayName` are a
    // curator+asset composite the recorder builds ("Gauntlet USDC"), and it
    // collapses distinct vaults: four Gauntlet-curated USDC vaults holding
    // $27M / $11M / $10M / $5.8M all rendered under that one label, as did two
    // Steakhouse ones at $75M and $15M. Worse, it MISATTRIBUTES — the
    // SwissBorg Morpho USDC vault is Gauntlet-CURATED, so the composite
    // renamed it "Gauntlet USDC" and presented someone else's product as
    // Gauntlet's.
    //
    // The real names were there all along and are distinct: "Gauntlet USDC
    // Core", "Gauntlet USDC Prime", "Gauntlet USDC RWA", "SwissBorg Morpho
    // USDC". Preferring them cuts chain-1 vault collisions from 19 groups (49
    // rows) to 5 (12). The curator still renders — it is its own field.
    // The vault's OWN name, VERBATIM — not through `earnMarketLabel`, whose
    // asset-prefix rule is written for lending markets and would render
    // "USDe · Strata srUSDe" here (a vault's share symbol is not its asset
    // symbol, so the "does the name state the asset?" test always fails).
    // Only the two suffixes that carry identity are applied.
    // The brand renders in the subtitle beside this, so a name that repeats it
    // says it twice ("Lido wstETH" under "Lido · Vaults").
    name: withTrancheLabel(
      withMaturityLabel(
        stripLeadingBrand(
          vaultDisplayName(row, info) ?? '',
          str(row.curatorName) ??
            venueBrand(vaultVenue(provider as VaultProvider)),
        ) || undefined,
        maturity,
      ),
      trancheFromCounterparty(str(meta.solvency)),
    ),
    ref: address,
    logoURI: str(info.logoURI) ?? str(row.underlyingInfo?.asset?.logoURI),

    asset: {
      address: underlying,
      symbol: str(row.underlyingInfo?.asset?.symbol) ?? '',
      decimals: assetDecimals,
      assetGroup: str(info.assetGroup),
      priceUsd: num(row.underlyingInfo?.prices?.priceUsd),
    },
    // The vault IS a share token by construction; carry it so a withdraw in
    // share units can be built without a second lookup.
    shareToken: {
      address,
      symbol: str(info.symbol) ?? str(row.symbol) ?? '',
      decimals: shareDecimals,
    },

    rate,
    // The vault origin reports RAW base units + a pre-formatted convenience
    // field. Carry both; fall back to formatting the raw amount ourselves so
    // `formatted` — the field consumers sort on — is never silently absent.
    tvl: amount(
      tvl.totalAssets,
      tvl.totalAssetsFormatted,
      tvl.totalAssetsUsd,
      assetDecimals,
    ),
    liquidity:
      liq.liquidity != null || liq.liquidityUsd != null
        ? amount(
            liq.liquidity,
            liq.liquidityFormatted,
            liq.liquidityUsd,
            assetDecimals,
          )
        : undefined,
    depositCapacity: str(meta.depositCapacity) ?? str(meta.maxDeposit),
    maturity,

    exit: {
      mode: exitMode,
      settlement: str(meta.redemptionType) as 'sync' | 'async' | undefined,
      cooldownSecs: num(meta.withdrawalCooldownSeconds),
      feeBps: num(meta.withdrawFeeBps),
      // Passed through, never re-derived: the earn row and the vault row must
      // not disagree about what the legs out are.
      routes: Array.isArray(meta.exitRoutes)
        ? (meta.exitRoutes as EarnExit['routes'])
        : undefined,
    },
    availability,
    risk: {
      yieldProfile: yieldProfile(info.yieldProfile),
      denomination: denomination(info.denomination),
      // The curated trust classification, where one exists. Typed on `EarnRisk`
      // since the surface was written and never populated — a tranche's
      // first-loss position is the term most worth surfacing on a rate list.
      counterparty: str(meta.solvency) as EarnRisk['counterparty'],
      score: num(row.rating?.score),
      label: str(row.rating?.level),
      illiquid: isIlliquid({
        exitMode,
        tvlUsd: num(tvl.totalAssetsUsd),
        liquidityUsd: num(liq.liquidityUsd),
      }),
    },

    // Filled by `capabilities.ts` — kept required on the type so a normalizer
    // that forgets to stamp them is a compile error, not an empty CTA.
    capabilities: [],
    ...(basket ? { basket } : {}),
    providerMeta: row.providerMeta,
  }

  return market
}

/**
 * Convert a source rate to PERCENT.
 *
 * `undefined` in ⇒ `undefined` out. Zero is preserved (a real 0 % is
 * meaningful — a collateral-only leg genuinely earns nothing) rather than
 * being folded into `undefined`.
 */
export function ratePercent(
  value: unknown,
  provider: string,
  fractionProviders: ReadonlySet<string> = FRACTION_RATE_PROVIDERS,
): number | undefined {
  const n = num(value)
  if (n === undefined) return undefined
  return fractionProviders.has(provider) ? n * 100 : n
}

/**
 * Cheap plausibility guard for the fraction/percent question.
 *
 * Not a correctness proof — a genuine 300 % vault exists and a genuine 0.02 %
 * one does too. It catches the systematic case: a whole provider's rows
 * landing three orders of magnitude off because the origin already normalized
 * and we normalized again. Call it from a test or a recorder, not per request.
 */
export function implausibleRatePercent(percent: number): boolean {
  return !Number.isFinite(percent) || percent < 0 || percent > 10_000
}

function resolveRateKind(
  provider: string,
  meta: Record<string, any>,
): RateKind {
  // A row whose share price is PUBLISHED from a NAV feed is `nav-accrual`, not
  // a governance-set rate — the distinction Re / Apyx turn on.
  //
  // This branch was unreachable until `navOracle` and `solvency` were surfaced
  // onto the vault row: the registry knew which entries used the `nav-oracle`
  // reader, but nothing downstream could see it, so every NAV-attested vault
  // reported as an ordinary managed rate on a solvent pool.
  if (meta.navOracle || meta.solvency === 'nav-attested') return 'nav-accrual'
  return RATE_KIND[provider] ?? 'variable-managed'
}

/**
 * The three fields a vault row's TERMS hang off, derived from its provider and
 * `providerMeta`.
 *
 * Exported because the origin's SQL route builds its own row shape and had
 * none of them: it hardcoded `kind: 'variable-curve'`, defaulted the exit to
 * `instant`, and emitted no maturity at all. On a Pendle PT — a zero-coupon
 * bond — that rendered as **"Variable 22.09% · withdraw any time"**, with the
 * tags `variable-rate, perpetual, exit-instant`. Every clause was false, and
 * the profile beside it still read `vault.fixed-maturity@v1`.
 *
 * That is the exact failure AGENTS.md records ("Pendle inheriting savings-vault
 * prose whose every clause was wrong for a fixed-maturity bond"), reintroduced
 * by re-deriving in SQL what this module already derives. One function, called
 * by both paths, is the fix that keeps them from drifting again.
 */
export function earnVaultTerms(
  provider: string,
  providerMeta: Record<string, any> | undefined,
  size?: {
    tvl?: VaultSourceRow['tvl']
    liquidity?: VaultSourceRow['liquidity']
  },
): {
  maturity?: MaturityTerms
  exitMode: SupplyExitMode
  rateKind: RateKind
} {
  const meta = providerMeta ?? {}
  return {
    maturity: resolveMaturity(meta),
    exitMode: resolveExitMode(provider, meta, size?.tvl, size?.liquidity),
    rateKind: resolveRateKind(provider, meta),
  }
}

/**
 * `canDeposit` is a PERMISSION; `depositCapacity` is ROOM. They are
 * independent: a vault can be permissionless and full (3Jane USD3 at
 * `maxDeposit = 0`), or gated and empty (KYC-minted RWA). Collapsing them
 * loses the reason, which is what the disabled CTA needs to render.
 */
function resolveAvailability(
  meta: Record<string, any>,
  maturity?: MaturityTerms,
): EarnAvailability {
  const isMintable = meta.isMintable
  const capacity = str(meta.depositCapacity) ?? str(meta.maxDeposit)
  const capFull = capacity === '0'

  let gating: EarnGating | undefined
  let reason: string | undefined
  // Maturity is checked FIRST and against the clock, never against a stored
  // flag. A matured fixed-term row still carries its last pre-expiry rate, so
  // if one slips past the upstream filter (a recorder generation behind, a
  // cached listing) it must at minimum not read as depositable.
  if (isMaturedTerm(maturity)) {
    gating = 'matured'
    reason = 'This market has reached maturity'
  } else if (isMintable === false) {
    gating = 'allowlist-contract'
    reason = 'Deposits are permissioned for this vault'
  } else if (capFull) {
    gating = 'cap-full'
    reason = 'Deposit cap is full'
  } else if (meta.paused === true) {
    gating = 'paused'
    reason = 'Vault is paused'
  }

  return {
    canDeposit: gating === undefined,
    // Exits stay open under a deposit cap, a mint allowlist or a maturity —
    // every wind-down we have integrated (Lista, dTRINITY/Sonic) keeps the
    // door open outward, and a matured bond is precisely the case where the
    // holder still needs out. An explicit pause is the one thing that shuts
    // both.
    // ...and so is an EMPTY route list. `exitRoutes` is derived for every row
    // and the invariant is "at least one route", so `[]` is never a default —
    // it is a reader saying every leg is shut (BitFi publishes its two queues
    // from live fee flags, and a chain can have both switched off). Absent is
    // the ordinary case and says nothing.
    canWithdraw:
      meta.paused !== true &&
      !(Array.isArray(meta.exitRoutes) && meta.exitRoutes.length === 0),
    gating,
    reason,
  }
}

/**
 * What to call a vault, in descending order of how much it says.
 *
 * **The UNDERLYING is the last resort, not the second option.** A vault's
 * share is normally a token in its own right with a name in the token lists —
 * `Wrapped liquid staked Ether 2.0` / `wstETH` — and naming the row after what
 * it holds instead throws that away: every Lido, Binance and EtherFi row came
 * out `ETH`, every Lagoon row `USDC`, indistinguishable from each other and
 * from the lending markets beside them.
 *
 * The ladder:
 *
 *   1. the vault's OWN name              `Lido wstETH`, `9Summits Flagship USDC`
 *   2. `vaultInfo.name` / `displayName`  the recorder's label
 *   3. the SHARE TOKEN's name            from the token lists
 *   4. the share SYMBOL                  `wstETH` — terse but still the vault
 *   5. (caller) the underlying           only when nothing above exists
 *
 * Rungs 3 and 4 were missing. Nothing falls through to them in today's data —
 * all 811 live vault rows carry a name — but they are what stops a newly
 * integrated provider that omits one from silently rendering as its deposit
 * asset, which is exactly how this was found.
 */
function vaultDisplayName(
  row: VaultSourceRow,
  info: Record<string, any>,
): string | undefined {
  const share = (row as any).shareAsset as
    | { name?: string; symbol?: string }
    | undefined
  return (
    str(row.name) ??
    str(info.name) ??
    str(row.displayName) ??
    str(share?.name) ??
    str(share?.symbol) ??
    str(info.symbol) ??
    str((row as any).symbol)
  )
}

/**
 * Lift a fixed-term row's maturity out of `providerMeta`.
 *
 * Reads `expiry` (unix seconds), which is what the Pendle provider publishes;
 * `maturity` is accepted as an alias so the fixed-term LENDERS map onto the
 * same field without a second code path when the term-sheet adapter reaches
 * them. Absent ⇒ the row is perpetual and no `maturity` block is emitted at
 * all (rather than `kind: 'perpetual'` on ~4,000 rows that never had a term).
 */
function resolveMaturity(meta: Record<string, any>): MaturityTerms | undefined {
  const expiry = num(meta.expiry) ?? num(meta.maturity)
  if (expiry === undefined || expiry <= 0) return undefined
  const nowSecs = Math.floor(Date.now() / 1000)
  return {
    kind: 'fixed-date',
    maturity: expiry,
    maturityIso: str(meta.expiryIso) ?? new Date(expiry * 1000).toISOString(),
    // A SNAPSHOT — a cached listing ages, so a countdown must be recomputed
    // from `maturity` rather than ticked down from this.
    secondsToMaturity: Math.max(expiry - nowSecs, 0),
    // A PT simply stops earning: it redeems for the underlying at par and
    // sits there. No penalty, no liquidation, no auto-roll.
    atMaturity: str(meta.atMaturity) ?? 'stops-earning',
  }
}

/**
 * Has a fixed-term row passed its maturity? Judged against the CLOCK, never
 * against a stored flag — a recorder that stops writing leaves the row frozen
 * at its last pre-expiry state, and a cached "live" boolean would keep
 * advertising a dead fixed rate forever. Mirrors `pendle_pt_is_live()` in the
 * recorder, deliberately.
 *
 * Exported because the ORIGIN builds its earn rows in its own route rather
 * than through {@link earnMarketFromVault}, and the two must agree — the same
 * reason {@link earnVaultTerms} is exported.
 */
export function isMaturedTerm(
  maturity?: MaturityTerms,
  nowSecs = Math.floor(Date.now() / 1000),
): boolean {
  if (!maturity?.maturity) return false
  return maturity.maturity <= nowSecs
}

/**
 * The forward rate a MATURED fixed-term row may publish, which is **zero**.
 *
 * Not a guard and not a clamp: it is the instrument's definition. A matured
 * principal token redeems for the underlying at par and then sits there — it
 * earns nothing from that moment on, so 0 is the measured answer, not a
 * comfortable default. (It is also the worst-ranking value, which is why this
 * is not the "absent rather than defaulted" case AGENTS.md warns about: there
 * is nothing we cannot fill.)
 *
 * ## Why this function exists at all
 *
 * Every layer that could have caught a matured row was scoped to skip one:
 *
 *  - the providers DROP matured markets (`pendleIncludeExpired` defaults
 *    false), so nothing downstream expected to see one;
 *  - `vaults/rateSanity.ts` bails on `expiry <= now` for exactly that reason;
 *  - the recorder's `pendle_vaults_latest` is UPSERT-ONLY, so when the
 *    provider stops returning the market the row does not disappear — it
 *    FREEZES at the last tick before expiry.
 *
 * That last tick is the worst possible one to freeze. A fixed-term APR is a
 * price deviation raised to `365 / daysToMaturity`, so in the final hours the
 * exponent amplifies rounding into anything: `PT apxUSD 27 Aug 2026` recorded
 * 8.28 % a fortnight out, 68 % at T−8 h, 236 % at T−1 h, and served **267.29 %
 * on $12.1 M** for weeks afterwards off a share price 0.3 bps from par. Across
 * chains that was 19 rows and $178.7 M of nominal TVL publishing APRs from
 * +267 % to −315 %, contaminating BOTH ends of every rate sort.
 *
 * So the rule is applied where the row is BUILT rather than where it is
 * filtered — a holder still needs the row to redeem, and a row that is served
 * must not carry a rate that stopped existing.
 */
export function earnRateAtMaturity(
  rate: EarnRate,
  maturity?: MaturityTerms,
  nowSecs?: number,
): EarnRate {
  if (!isMaturedTerm(maturity, nowSecs)) return rate
  return {
    ...rate,
    total: 0,
    // Every yield leg, not just the headline: a consumer that re-sums the legs
    // (or ranks on `marketOwn`, as the unified tab's second sort does) would
    // otherwise get the stale number back through the side door.
    ...(rate.base !== undefined ? { base: 0 } : {}),
    ...(rate.rewards !== undefined ? { rewards: 0 } : {}),
    ...(rate.intrinsic !== undefined ? { intrinsic: 0 } : {}),
    ...(rate.marketOwn !== undefined ? { marketOwn: 0 } : {}),
    // `fee`, `kind` and `source` are untouched: the fee schedule and the
    // mechanism are still facts about the instrument, and calling a matured
    // bond's rate anything but `fixed-term` would lose what it was.
  }
}

function brandFallback(provider: string): string | undefined {
  switch (provider) {
    case 'fluid':
      return 'Fluid'
    case 'euler-earn':
      return 'Euler'
    case 'aave-earn':
      return 'Aave'
    case 'gearbox':
      return 'Gearbox'
    case 'gmx':
      return 'GMX'
    case 'hypercore':
      return 'HyperLiquid'
    default:
      return undefined
  }
}

/**
 * Build an `EarnAmount` from the vault origin's raw + formatted pair.
 *
 * `raw` stays a STRING (base-unit integers overflow float64). `formatted` is
 * derived from `raw` when the origin omits its convenience field, so the
 * cross-source comparison field is populated for every provider rather than
 * only the ones that happen to send it.
 */
function amount(
  raw: unknown,
  formatted: unknown,
  usd: unknown,
  decimals: number,
): EarnAmount {
  const rawStr = raw != null ? String(raw) : undefined
  return {
    raw: rawStr,
    formatted: num(formatted) ?? formatRaw(rawStr, decimals),
    usd: num(usd),
  }
}

/** Attach the registry's curator entity, but only where a curator resolved. */
function withCuratorEntity(
  id: ReturnType<typeof resolveEarnIdentity>,
  entity: string | undefined,
): ReturnType<typeof resolveEarnIdentity> {
  if (!id.curator || !entity) return id
  return { ...id, curator: { ...id.curator, entity } }
}

function sum(a?: number, b?: number): number | undefined {
  if (a === undefined && b === undefined) return undefined
  return (a ?? 0) + (b ?? 0)
}
